import type { Project } from "@/models/project.model"
import { isPopulated, lazyFieldId } from "@/models/types/lazy-field"

/** How closely a project relates to a reference project, closest first. */
export enum ProjectRelation {
  SAME_PROJECT = 0,
  SAME_EXECUTOR = 1,
  SAME_BUSINESS_ENTITY = 2,
  UNRELATED = 3
}

/** Known business entity of the project's executor, if the executor has been loaded. */
function getExecutorBusinessEntityId(project: Project): string | undefined {
  return isPopulated(project.executor)
    ? lazyFieldId(project.executor.knownBusinessEntity)
    : undefined
}

/**
 * Relation of `project` to `reference`: the same project, a project with the same executor,
 * or a project whose executor belongs to the same known business entity.
 */
export function getProjectRelation(
  project: Project | null | undefined,
  reference: Project | null | undefined
): ProjectRelation {
  if (!project || !reference) return ProjectRelation.UNRELATED
  if (project.id === reference.id) return ProjectRelation.SAME_PROJECT

  const executorId = lazyFieldId(reference.executor)
  if (executorId && lazyFieldId(project.executor) === executorId) {
    return ProjectRelation.SAME_EXECUTOR
  }

  const businessEntityId = getExecutorBusinessEntityId(reference)
  if (businessEntityId && getExecutorBusinessEntityId(project) === businessEntityId) {
    return ProjectRelation.SAME_BUSINESS_ENTITY
  }
  return ProjectRelation.UNRELATED
}

/**
 * Sort projects by how closely they relate to `reference` (see {@link getProjectRelation}),
 * then alphabetically by name. Returns 0 when both rank the same and share a name.
 */
export function compareProjectsByRelation(
  a: Project | null | undefined,
  b: Project | null | undefined,
  reference: Project | null | undefined
): number {
  if (reference) {
    const relationComparison = getProjectRelation(a, reference) - getProjectRelation(b, reference)
    if (relationComparison !== 0) return relationComparison
  }
  return (a?.name || "").localeCompare(b?.name || "")
}
